import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Download, Plus, Trash2, Globe, ClipboardPaste } from 'lucide-react';
import { fetchWmsCapabilities, parseWmsCapabilitiesXml, updateMapLayers } from '../../services/api';

const parseCapabilitiesInBrowser = (xmlText) => {
  const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
  if (doc.querySelector('parsererror')) throw new Error('XML nav derīgs');
  const layers = [];
  doc.querySelectorAll('Layer').forEach((node) => {
    const name = Array.from(node.children).find((c) => c.localName === 'Name')?.textContent?.trim();
    const title = Array.from(node.children).find((c) => c.localName === 'Title')?.textContent?.trim();
    if (name) layers.push({ name, title: title || name });
  });
  return { layers, service_title: doc.querySelector('Service > Title')?.textContent?.trim() };
};

const capabilitiesUrl = (url) => `${url}${url.endsWith('?') || url.endsWith('&') ? '' : url.includes('?') ? '&' : '?'}SERVICE=WMS&REQUEST=GetCapabilities&VERSION=1.1.1`;

export const MapLayerSettingsDialog = ({ open, onOpenChange, mapLayers }) => {
  const [configured, setConfigured] = useState([]);
  const [sourceUrl, setSourceUrl] = useState('');
  const [discovered, setDiscovered] = useState([]);
  const [discoverError, setDiscoverError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteXml, setPasteXml] = useState('');
  const [manual, setManual] = useState({ title: '', layers: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfigured(mapLayers.layers.map((l) => ({ ...l })));
    setSourceUrl((prev) => prev || mapLayers.defaultSource || '');
    setDiscovered([]);
    setDiscoverError('');
    setShowPaste(false);
  }, [open, mapLayers.layers, mapLayers.defaultSource]);

  const discover = async () => {
    if (!sourceUrl.trim()) return;
    setLoading(true);
    setDiscoverError('');
    setDiscovered([]);
    try {
      const data = await fetchWmsCapabilities(sourceUrl.trim());
      setDiscovered(data.layers || []);
      toast.success(`Nolasīti ${data.layers?.length || 0} slāņi (caur serveri)`);
    } catch (serverError) {
      try {
        const response = await fetch(capabilitiesUrl(sourceUrl.trim()), { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const parsed = parseCapabilitiesInBrowser(await response.text());
        setDiscovered(parsed.layers);
        toast.success(`Nolasīti ${parsed.layers.length} slāņi (caur pārlūku)`);
      } catch (browserError) {
        setDiscoverError(`${serverError.response?.data?.detail || 'Serveris nevar sasniegt WMS.'} Pārlūks: ${browserError.message}. Ielīmējiet GetCapabilities XML vai ievadiet slāņus manuāli.`);
        setShowPaste(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const parsePasted = async () => {
    try {
      let parsed;
      try {
        parsed = parseCapabilitiesInBrowser(pasteXml);
      } catch (error) {
        parsed = await parseWmsCapabilitiesXml(pasteXml);
      }
      setDiscovered(parsed.layers || []);
      setDiscoverError('');
      toast.success(`Nolasīti ${parsed.layers?.length || 0} slāņi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās nolasīt XML');
    }
  };

  const addLayer = (title, layersName) => {
    if (!layersName.trim()) return;
    if (configured.some((l) => l.url === sourceUrl.trim() && l.layers === layersName.trim())) {
      toast.info('Šis slānis jau ir pievienots');
      return;
    }
    setConfigured((prev) => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: title.trim() || layersName.trim(),
      url: sourceUrl.trim(),
      layers: layersName.trim(),
      format: 'image/png',
      transparent: true,
      opacity: 0.8,
      version: '1.1.1',
      enabled_default: false,
      attribution: 'LVM GEO'
    }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateMapLayers(configured);
      toast.success('Kartes slāņi saglabāti');
      await mapLayers.reload();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt slāņus');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="map-layer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-[#1F3D2B]" /> Kartes slāņi (WMS)</DialogTitle>
          <DialogDescription>LVM GEO vektordatu WMS un citi WMS servisi. Slāņi tiek zīmēti kartē virs OpenStreetMap; redz administratori un darbinieki.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="rounded-xl border border-[#1F3D2B]/10 bg-[#f8faf8] p-4 space-y-3">
            <Label>WMS servisa URL</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://geoserver.lvmgeo.lv/wmsvector...?" className="font-mono text-xs" data-testid="wms-source-url" />
              <Button onClick={discover} disabled={loading || !sourceUrl.trim()} className="bg-[#1F3D2B] hover:bg-[#2a5039] shrink-0" data-testid="wms-discover">
                <Download className="w-4 h-4 mr-2" /> {loading ? 'Nolasa...' : 'Nolasīt slāņus'}
              </Button>
            </div>
            {discoverError && <p className="text-xs text-red-700 whitespace-pre-line" data-testid="wms-discover-error">{discoverError}</p>}
            {(showPaste || discoverError) && (
              <div className="space-y-2">
                <button type="button" onClick={() => setShowPaste((v) => !v)} className="text-xs text-[#1F3D2B] underline flex items-center gap-1" data-testid="wms-paste-toggle"><ClipboardPaste className="w-3.5 h-3.5" /> Ielīmēt GetCapabilities XML</button>
                {showPaste && (
                  <div className="space-y-2">
                    <p className="text-xs text-[#2E2E2E]/60">Atveriet pārlūkā: <a href={capabilitiesUrl(sourceUrl.trim() || 'https://')} target="_blank" rel="noreferrer" className="underline break-all">{capabilitiesUrl(sourceUrl.trim() || '')}</a>, nokopējiet visu XML un ielīmējiet zemāk.</p>
                    <Textarea rows={4} value={pasteXml} onChange={(e) => setPasteXml(e.target.value)} placeholder="<WMT_MS_Capabilities ...>" className="font-mono text-xs" data-testid="wms-paste-xml" />
                    <Button size="sm" variant="outline" onClick={parsePasted} disabled={!pasteXml.trim()} data-testid="wms-paste-parse">Nolasīt no XML</Button>
                  </div>
                )}
              </div>
            )}
            {discovered.length > 0 && (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y" data-testid="wms-discovered-list">
                {discovered.map((layer) => (
                  <div key={layer.name} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{layer.title}</p>
                      <p className="text-xs text-[#2E2E2E]/50 font-mono truncate">{layer.name}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => addLayer(layer.title, layer.name)} data-testid={`wms-add-${layer.name}`}><Plus className="w-3.5 h-3.5 mr-1" /> Pievienot</Button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end pt-1">
              <div className="space-y-1">
                <Label className="text-xs">Manuāli: nosaukums</Label>
                <Input value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} placeholder="Mežaudzes" data-testid="wms-manual-title" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">WMS slānis (LAYERS parametrs)</Label>
                <Input value={manual.layers} onChange={(e) => setManual({ ...manual, layers: e.target.value })} placeholder="lvm:mezaudzes" className="font-mono text-xs" data-testid="wms-manual-layers" />
              </div>
              <Button variant="outline" onClick={() => { addLayer(manual.title, manual.layers); setManual({ title: '', layers: '' }); }} disabled={!manual.layers.trim() || !sourceUrl.trim()} data-testid="wms-manual-add"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-[#1F3D2B]">Konfigurētie slāņi ({configured.length})</p>
            {configured.length === 0 ? (
              <p className="text-sm text-[#2E2E2E]/60" data-testid="wms-configured-empty">Vēl nav pievienots neviens slānis.</p>
            ) : (
              <div className="divide-y rounded-lg border border-gray-200" data-testid="wms-configured-list">
                {configured.map((layer, index) => (
                  <div key={layer.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_90px_auto_auto] gap-2 items-center px-3 py-2 text-sm" data-testid={`wms-configured-${layer.id}`}>
                    <Input value={layer.title} onChange={(e) => setConfigured((prev) => prev.map((l, i) => (i === index ? { ...l, title: e.target.value } : l)))} className="h-8" data-testid={`wms-configured-title-${layer.id}`} />
                    <Input value={layer.layers} onChange={(e) => setConfigured((prev) => prev.map((l, i) => (i === index ? { ...l, layers: e.target.value } : l)))} className="h-8 font-mono text-xs" data-testid={`wms-configured-layers-${layer.id}`} />
                    <Input type="number" min="0.1" max="1" step="0.1" value={layer.opacity} onChange={(e) => setConfigured((prev) => prev.map((l, i) => (i === index ? { ...l, opacity: Number(e.target.value) } : l)))} className="h-8" title="Caurspīdīgums" />
                    <label className="flex items-center gap-1.5 text-xs whitespace-nowrap"><input type="checkbox" checked={layer.enabled_default} onChange={(e) => setConfigured((prev) => prev.map((l, i) => (i === index ? { ...l, enabled_default: e.target.checked } : l)))} className="accent-[#1F3D2B]" /> Ieslēgts pēc nokl.</label>
                    <Button size="icon" variant="ghost" className="text-red-600 h-8 w-8" onClick={() => setConfigured((prev) => prev.filter((_, i) => i !== index))} data-testid={`wms-configured-delete-${layer.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wms-cancel">Atcelt</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="wms-save">{saving ? 'Saglabā...' : 'Saglabāt slāņus'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MapLayerSettingsDialog;
